import styled from 'styled-components';
import { BottomBar, BottomBarButton } from '../Activity/BottomActionBar';
import { PExtraSmall } from '../elements/Text';
import { SmallPortalTooltip } from '../Tooltip';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../Admin/Progress/Tooltip';
import React from 'react';
import { StepTimer, TotalTimer } from './TimerDisplay';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useTheme } from 'styled-components';
import {
  AssessmentTestContext,
  SyncStatus,
} from '../../contexts/assessment-test';
import ConfirmNextModal from './ConfirmNextModal';
import { useActiveTestStep } from '../../hooks/assessment-test/use-active-step';
import {
  TEST_EXPIRATION_READABLE,
  STEP_EXPIRATION_WARNING_READABLE,
  TEST_EXPIRATION_WARNING_READABLE,
} from './TestTimeoutHandler';
import { isSelfAssessmentStep } from '../../contexts/assessment-test/helpers';

export default function BottomActionBar() {
  const [state, dispatch] = React.useContext(AssessmentTestContext);
  const numStepsTotal = state.steps.length;
  const [isConfirmNextModalOpen, setIsConfirmNextModalOpen] =
    React.useState(false);

  const [showTimers, setShowTimers] = React.useState(true);
  const toggleTimers = () => setShowTimers((v) => !v);

  const animOptions = React.useRef({
    duration: 300,
    easing: 'ease-in-out',
  }).current;
  const [animRef] = useAutoAnimate(animOptions);

  const theme = useTheme();
  const { stepIndex } = useActiveTestStep();
  const numSteps = state.steps.length;
  const currentStepIndex = stepIndex;

  const isLastStep = currentStepIndex === numSteps - 1;
  const hasAnsweredAllQuestionsInStep = state.steps[
    currentStepIndex
  ].sections.every((s) => s.isAnswered && s.didInteract);

  const handleNext = () => {
    if (isLastStep) {
      dispatch({ type: 'FINISH_TEST', timestamp: new Date() });
    } else {
      dispatch({ type: 'GO_TO_NEXT_STEP', timestamp: new Date() });
    }
  };

  const handleNextWithConfirmation = () => {
    if (hasAnsweredAllQuestionsInStep) {
      handleNext();
    } else {
      setIsConfirmNextModalOpen(true);
    }
  };

  const missedQuestions = state.steps
    .filter((_, index) => index <= currentStepIndex)
    .map((step, index) => {
      return step.sections
        .filter((section) => !section.isAnswered && !section.didInteract)
        .map((_, sectionIndex) => {
          return {
            stepIndex: index,
            questionIndex: sectionIndex,
          };
        });
    })
    .flat();

  const isSelfAssessment = isSelfAssessmentStep(state.steps[stepIndex]);

  return (
    <>
      <BottomBar>
        <BottomBarGrid>
          <BottomBarLeft ref={animRef}>
            {!isSelfAssessment && (
              <TimerToggleButton
                onClick={toggleTimers}
                data-tooltip-id="toggle-timers-tooltip"
                className={`fa-solid ${showTimers ? 'fa-eye-slash' : 'fa-eye'}`}
              >
                <SmallPortalTooltip
                  anchorSelect="[data-tooltip-id='toggle-timers-tooltip']"
                  place="top"
                >
                  <TooltipContent>
                    <TooltipDetail>
                      {showTimers ? 'Hide timers' : 'Show timers'}
                    </TooltipDetail>
                  </TooltipContent>
                </SmallPortalTooltip>
              </TimerToggleButton>
            )}
            {showTimers && !isSelfAssessment && (
              <TimersWrapper>
                <TimerItem data-tooltip-id="time-on-this-step-tooltip">
                  <TimerLabel>This Step:</TimerLabel>
                  <StepTimer />
                </TimerItem>
                <SmallPortalTooltip
                  style={{ width: 300 }}
                  anchorSelect="[data-tooltip-id='time-on-this-step-tooltip']"
                  place="top"
                >
                  <TooltipContent>
                    <TooltipTitle>Time spent on this step</TooltipTitle>
                    <TooltipDetail>
                      We recommend you to spend around{' '}
                      {STEP_EXPIRATION_WARNING_READABLE} on each step.
                    </TooltipDetail>
                  </TooltipContent>
                </SmallPortalTooltip>
                <TimerItem data-tooltip-id="total-time-tooltip">
                  <TimerLabel>Total:</TimerLabel>
                  <TotalTimer />
                </TimerItem>
                <SmallPortalTooltip
                  style={{ width: 300 }}
                  anchorSelect="[data-tooltip-id='total-time-tooltip']"
                  place="top"
                >
                  <TooltipContent>
                    <TooltipTitle>Time spent on the test</TooltipTitle>
                    <TooltipDetail>
                      The test was designed to take around{' '}
                      {TEST_EXPIRATION_WARNING_READABLE} minutes, but you can
                      spend up to {TEST_EXPIRATION_READABLE}
                    </TooltipDetail>
                  </TooltipContent>
                </SmallPortalTooltip>
              </TimersWrapper>
            )}
            <SavingIndicator />
          </BottomBarLeft>

          <BottomBarCenterGrid>
            <StepProgress
              data-tooltip-id="assessment-step-progress-tooltip"
              aria-label="Assessment progress"
            >
              <StepDotsContainer>
                {Array.from({ length: numSteps }).map((_, index) => {
                  const isFilled = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  return (
                    <StepDot
                      key={`step-dot-${index}`}
                      isFilled={isFilled}
                      isCurrent={isCurrent}
                    />
                  );
                })}
              </StepDotsContainer>
            </StepProgress>
            <SmallPortalTooltip
              anchorSelect="[data-tooltip-id='assessment-step-progress-tooltip']"
              place="top"
            >
              <TooltipContent>
                <TooltipTitle>Test Progress</TooltipTitle>
                <TooltipDetail>
                  You are on step {currentStepIndex + 1} of {numStepsTotal}
                </TooltipDetail>
              </TooltipContent>
            </SmallPortalTooltip>
          </BottomBarCenterGrid>

          <BottomBarRight>
            <BottomBarButton
              label="Go Back"
              style={{ minWidth: '150px' }}
              type="secondary"
              faIcon="fa-solid fa-arrow-left"
              onClick={() =>
                dispatch({ type: 'GO_TO_PREVIOUS_STEP', timestamp: new Date() })
              }
              isDisabled={currentStepIndex === 0}
              showIconBeforeLabel
              color={theme.colors.textRegular}
            />
            <BottomBarButton
              label={
                currentStepIndex === numStepsTotal - 1 ? 'Finish' : 'Next Step'
              }
              style={{ minWidth: '150px' }}
              type="primary"
              faIcon="fa-solid fa-arrow-right"
              isDisabled={false}
              onClick={handleNextWithConfirmation}
            />
          </BottomBarRight>
        </BottomBarGrid>
      </BottomBar>
      <ConfirmNextModal
        isOpen={isConfirmNextModalOpen}
        onClose={() => setIsConfirmNextModalOpen(false)}
        goToNextStep={handleNext}
        isLastStep={isLastStep}
        missedQuestions={missedQuestions}
      />
    </>
  );
}

export const StepProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const StepDotsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StepDot = styled.div<{
  isFilled: boolean;
  isCurrent: boolean;
}>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme, isFilled }) =>
      isFilled ? theme.colors.primary : theme.colors.bgActive};
  background-color: ${({ theme, isFilled }) =>
    isFilled ? theme.colors.primary : theme.colors.bgDefaultSecondary};
  outline: ${({ theme, isCurrent }) =>
    isCurrent ? `2px solid ${theme.colors.bgActive}` : 'none'};
  outline-offset: 2px;
`;

export const TimersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0;
`;

export const TimerItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

export const TimerLabel = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 400;
  margin-top: 0;
  padding-bottom: 0;
`;

export const TimerValue = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
  margin-top: 0;
  padding-bottom: 0;
`;

export const TimerToggleButton = styled.i`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: pointer;
  border-radius: 6px;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  font-size: 14px;

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.textActive};
  }
`;

export const BottomBarCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  margin: auto 0;
  align-items: center;
  gap: 8px;
`;

export const BottomBarGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  width: 100%;
`;

export const BottomBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const BottomBarCenterGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BottomBarRight = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

type StatusProps = {
  status: 'saving' | 'saved' | 'error';
};
const SaveIcon = styled.i.attrs<StatusProps>(({ status }: StatusProps) => ({
  className:
    status === 'saving'
      ? `fa-solid fa-floppy-disk fa-fade`
      : status === 'saved'
      ? `fa-solid fa-check-double`
      : `fa-solid fa-floppy-disk-circle-xmark`,
}))<StatusProps>`
  color: ${({ theme }) => theme.colors.textRegular};
  border-radius: 6px;
  padding: 0;
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  font-size: 14px;
  --fa-animation-duration: 2s;
  --fa-fade-opacity: 0.6;
  margin-bottom: 1px;

  &:hover {
    color: ${({ theme }) => theme.colors.textActive};
  }
`;

export function SavingIndicator() {
  const [state] = React.useContext(AssessmentTestContext);
  const [status, setStatus] =
    React.useState<'saving' | 'saved' | 'error' | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (
      state.sync.status === SyncStatus.Queued ||
      state.sync.status === SyncStatus.Syncing
    ) {
      if (state.sync.backoffAttempt === 0) {
        setStatus('saving');
      } else {
        setStatus('error');
      }
    } else if (
      state.sync.status === SyncStatus.Idle &&
      state.sync.lastSyncedAt
    ) {
      setStatus('saved');
      timeoutRef.current = setTimeout(() => {
        setStatus(null);
        timeoutRef.current = null;
      }, 3000);
    }
  }, [
    state.sync.status,
    state.sync.lastSyncedAt,
    state.sync.backoffAttempt,
    state.sync.error,
  ]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (status === null) {
    return null;
  }

  return (
    <React.Fragment key={status}>
      <SaveIcon status={status} data-tooltip-id="saving-indicator-tooltip" />
      <SmallPortalTooltip
        anchorSelect="[data-tooltip-id='saving-indicator-tooltip']"
        place="top"
      >
        <TooltipContent>
          <TooltipTitle>Autosave</TooltipTitle>
          <TooltipDetail>
            {status === 'saving'
              ? 'Saving your progress'
              : status === 'saved'
              ? 'Progress saved'
              : 'Error saving your progress. Retrying...'}
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
    </React.Fragment>
  );
}
